import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import { getWordOfTheDay } from '../../data/jewishWords';
import { activities } from '../../data/content';
import { getExperienceType } from '../../data/experienceTypes';
import { getDaysUntil, calculateProgress } from '../../utils/experiences';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScroll, faBook, faGift, faCalendar, faUsers, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { getCurrentWeeklyReading, getUpcomingHolidays } from '../../utils/hebrewCalendar';
import { mapHolidayToExperienceType } from '../../utils/feedAlgorithm';
import ParashaDetailModal from './ParashaDetailModal';
import WordDetailModal from './WordDetailModal';
import { getExperienceIcon } from '../experiences/ExperienceTimelineCard';


function calculateDaysUntil(experience) {
  if (!experience?.date) return null;
  const date = experience.date.toDate ? experience.date.toDate() : new Date(experience.date);
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= 0 ? diffDays : null;
}

export default function ThisWeekHorizontal({ 
  onShabbatClick, 
  onWordClick, 
  onActivityClick,
  kidsAges = [] 
}) {
  const [activeExperience, setActiveExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showParashaModal, setShowParashaModal] = useState(false);
  const [showWordModal, setShowWordModal] = useState(false);
  const [hasExperienceForHoliday, setHasExperienceForHoliday] = useState(false);
  const { familyId } = useFamily();
  const navigate = useNavigate();
  
  const wordOfTheDay = getWordOfTheDay();
  const weeklyReading = getCurrentWeeklyReading();
  const upcomingHolidays = getUpcomingHolidays(10);
  // Get next major holiday (skip Rosh Chodesh)
  const nextHoliday = upcomingHolidays.find(h => !h.isFullMoon && h.isMajorHoliday) || upcomingHolidays.find(h => !h.isFullMoon) || upcomingHolidays[0];
  
  // Find age-appropriate activity
  const getAgeAppropriateActivity = () => {
    if (kidsAges.length === 0) return activities[0];
    const avgAge = kidsAges.reduce((sum, age) => sum + age, 0) / kidsAges.length;
    const suitableActivity = activities.find(activity => {
      if (!activity.ageRange) return true;
      const [min, max] = activity.ageRange.split('-').map(Number);
      return avgAge >= min && avgAge <= max;
    });
    return suitableActivity || activities[0];
  };

  const weeklyActivity = getAgeAppropriateActivity();

  // Load active experience
  useEffect(() => {
    if (!familyId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.docs.length > 0) {
        const exp = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        };
        const daysUntil = calculateDaysUntil(exp);
        if (daysUntil !== null && daysUntil >= 0) {
          setActiveExperience(exp);
        }
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading experience:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [familyId]);

  // Check if user already has an experience for the next holiday
  useEffect(() => {
    if (!familyId || !nextHoliday) {
      setHasExperienceForHoliday(false);
      return;
    }

    const experienceType = mapHolidayToExperienceType(nextHoliday.name);
    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId),
      where('type', '==', experienceType)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setHasExperienceForHoliday(snapshot.docs.length > 0);
    }, (error) => {
      console.error('Error checking experiences for holiday:', error);
      setHasExperienceForHoliday(false);
    });

    return unsubscribe;
  }, [familyId, nextHoliday]);

  const handleHolidayClick = () => {
    if (!nextHoliday) return;
    
    if (hasExperienceForHoliday) {
      // Navigate to experiences page
      navigate('/experiences');
    } else {
      // Create new experience for this holiday
      const experienceType = mapHolidayToExperienceType(nextHoliday.name);
      window.dispatchEvent(new CustomEvent('createExperience', { 
        detail: { 
          type: experienceType, 
          holidayName: nextHoliday.name,
          date: nextHoliday.date
        } 
      }));
    }
  };

  return (
    <div className="border-t border-border bg-white/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex flex-col md:flex-row justify-center gap-3 md:gap-3">
        
        {/* Shabbat Card - Enhanced with Parasha */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowParashaModal(true)}
          className="group flex items-center gap-3 px-4 py-3 md:py-2 rounded-lg hover:bg-surface hover:shadow-sm border border-transparent hover:border-border transition-all text-left touch-target active:bg-gray-50 relative"
        >
          <FontAwesomeIcon icon={faScroll} className="text-primary text-lg" />
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-primary mb-0.5">
              This Shabbat
            </h3>
            {weeklyReading ? (
              <div>
                {/* Line 2: Hebrew name with English in brackets */}
                <div className="text-sm font-semibold text-text-primary mb-0.5">
                  {weeklyReading.hebrewName} ({weeklyReading.englishName})
                </div>
                {/* Line 3: Main idea */}
                <div className="text-xs text-text-secondary mb-0.5">
                  {weeklyReading.mainIdea}
                </div>
                {/* Line 4: Haftarah */}
                <div className="text-xs text-text-secondary italic">
                  {weeklyReading.haftarah.source.split(' ')[0]} • {weeklyReading.haftarah.mainIdea}
                </div>
              </div>
            ) : (
              <div className="text-sm font-semibold text-text-primary">
                Special Shabbat
              </div>
            )}
          </div>
        </motion.button>
        
        {/* Hebrew Word Card - Enhanced with meaning */}
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowWordModal(true)}
          className="group flex items-center gap-3 px-4 py-3 md:py-2 rounded-lg hover:bg-surface hover:shadow-sm border border-transparent hover:border-border transition-all text-left touch-target active:bg-gray-50"
        >
          <FontAwesomeIcon icon={faBook} className="text-primary text-lg" />
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-primary mb-0.5">
              Word of the Day
            </h3>
            <div>
              {/* Line 2: Hebrew word and transliteration */}
              <div className="text-sm font-semibold text-text-primary mb-0.5">
                <span className="font-serif" dir="rtl">
                  {wordOfTheDay.hebrew}
                </span>
                {' '}
                <span>({wordOfTheDay.transliteration})</span>
              </div>
              {/* Line 3: Meaning */}
              <div className="text-xs text-text-secondary mb-0.5">
                {wordOfTheDay.meaning}
              </div>
              {/* Line 4: Language */}
              {wordOfTheDay.language && (
                <div className="text-xs text-text-secondary italic">
                  in {wordOfTheDay.language}
                </div>
              )}
            </div>
          </div>
        </motion.button>
        
        {/* Next Holiday Card */}
        {nextHoliday ? (
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleHolidayClick}
            className="group flex items-center gap-3 px-4 py-3 md:py-2 rounded-lg hover:bg-surface hover:shadow-sm border border-transparent hover:border-border transition-all text-left touch-target active:bg-gray-50"
          >
            <FontAwesomeIcon icon={faCalendar} className="text-primary text-lg" />
            <div className="flex-1 min-w-0">
              {/* Line 1: Coming Up */}
              <h3 className="text-xs font-semibold text-primary mb-0.5">
                Coming Up
              </h3>
              {/* Line 2: Holiday name */}
              <div className="text-sm font-semibold text-text-primary mb-0.5">
                {nextHoliday.name}
              </div>
              {/* Line 3: Description placeholder - could be enhanced later */}
              <div className="text-xs text-text-secondary mb-0.5">
                {nextHoliday.isMajorHoliday ? 'Major Jewish holiday' : 'Upcoming celebration'}
              </div>
              {/* Line 4: Date and days away */}
              <div className="text-xs text-text-secondary italic">
                {nextHoliday.date ? nextHoliday.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} • {nextHoliday.daysUntil} days away
              </div>
            </div>
          </motion.button>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 md:py-2">
            <div className="text-xs text-text-secondary">No upcoming holidays</div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ParashaDetailModal
        isOpen={showParashaModal}
        onClose={() => setShowParashaModal(false)}
        parasha={weeklyReading}
      />

      <WordDetailModal
        isOpen={showWordModal}
        onClose={() => setShowWordModal(false)}
        word={wordOfTheDay}
      />
    </div>
  );
}
